import { Link } from "react-router-dom";
import { Phone, Smartphone, Mail, MapPin, ExternalLink } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteData";
import logo from "@/assets/logo-painur-small.avif";
import wazeIcon from "@/assets/waze-small.png";

const FacebookIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
  </svg>
);

const InstagramIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

export default function Footer() {
  const CLINIC = useSiteSettings();
  return (
    <footer className="relative mt-24 border-t border-border/50 bg-background/50 backdrop-blur-sm">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-l from-transparent via-primary/50 to-transparent" />
      <div className="container mx-auto px-4 py-14">
        {/* Top row: brand + Waze CTA */}
        <div className="flex flex-col md:flex-row items-center md:items-center justify-center md:justify-between gap-6 mb-10 pb-10 border-b border-border/50 text-center md:text-start">
          <div className="flex items-center gap-3">
            <img src={logo} alt="בית הרופאים באקה - מרפאת כאב ושיקום" width={80} height={80} className="h-20 md:h-12 w-auto" />
            <div className="text-start">
              <div className="text-xl font-bold text-gradient leading-none">{CLINIC.name}</div>
              <div className="text-[10px] text-muted-foreground tracking-widest uppercase mt-1">{CLINIC.tagline}</div>
            </div>
          </div>

          <a
            href={CLINIC.waze}
            target="_blank"
            rel="noreferrer"
            aria-label="ניווט אל בית הרופאים באקה באמצעות וייז"
            className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/40 hover:border-primary hover:from-primary/20 hover:to-secondary/20 hover:shadow-[0_0_25px_hsl(var(--primary)/0.3)] transition-all"
          >
            <img src={wazeIcon} alt="אייקון וייז של בית הרופאים באקה" width={28} height={28} loading="lazy" decoding="async" className="h-7 w-7" />
            <div className="text-start">
              <div className="text-xs text-muted-foreground">ניווט אלינו</div>
              <div className="text-sm font-bold">פתח ב-Waze</div>
            </div>
          </a>
        </div>

        <div className="grid gap-8 sm:gap-10 grid-cols-2 md:grid-cols-3">
          {/* About */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="font-bold mb-4 text-foreground text-base">אודות המרפאה</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              מרפאה רב-תחומית לטיפול בכאב, פיזיותרפיה, ריפוי בעיסוק ושיקום. צוות רפואי מומחה
              וטיפולים מתקדמים — להחזרתכם לחיים מלאים.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={CLINIC.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="פייסבוק"
                className="h-9 w-9 rounded-full border border-border/60 bg-background/50 flex items-center justify-center text-muted-foreground hover:text-primary-foreground hover:border-transparent hover:bg-gradient-to-br hover:from-primary hover:to-secondary transition-all"
              >
                <FacebookIcon className="h-4 w-4" />
              </a>
              <a
                href={CLINIC.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="אינסטגרם"
                className="h-9 w-9 rounded-full border border-border/60 bg-background/50 flex items-center justify-center text-muted-foreground hover:text-primary-foreground hover:border-transparent hover:bg-gradient-to-br hover:from-primary hover:to-secondary transition-all"
              >
                <InstagramIcon className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-bold mb-4 text-foreground text-base">ניווט</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/clinic" className="hover:text-primary transition-colors">בית</Link></li>
              <li><Link to="/services" className="hover:text-primary transition-colors">טיפולים</Link></li>
              <li><Link to="/physiotherapy-baqa-al-gharbiyye" className="hover:text-primary transition-colors">פיזיותרפיה בבאקה</Link></li>
              <li><Link to="/team" className="hover:text-primary transition-colors">הצוות</Link></li>
              <li><Link to="/articles" className="hover:text-primary transition-colors">מאמרים</Link></li>
              <li><Link to="/about" className="hover:text-primary transition-colors">אודות</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors">יצירת קשר</Link></li>
              <li><Link to="/accessibility-statement" className="hover:text-primary transition-colors">הצהרת נגישות</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">מדיניות פרטיות</Link></li>
              <li><Link to="/terms-of-use" className="hover:text-primary transition-colors">תקנון שימוש</Link></li>
              <li>
                <a
                  href="https://college.painur.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                >
                  קורסים והכשרות <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold mb-4 text-foreground text-base">צרו קשר</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <a href={`tel:${CLINIC.phone}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Phone className="h-4 w-4 text-primary shrink-0" />
                  <span dir="ltr">{CLINIC.phone}</span>
                  <span className="text-xs text-muted-foreground/70">(משרד)</span>
                </a>
              </li>
              <li>
                <a href={`tel:${CLINIC.mobile}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Smartphone className="h-4 w-4 text-primary shrink-0" />
                  <span dir="ltr">{CLINIC.mobile}</span>
                  <span className="text-xs text-muted-foreground/70">(נייד)</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CLINIC.email}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Mail className="h-4 w-4 text-primary shrink-0" />
                  {CLINIC.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>{CLINIC.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-border/50 text-center text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {CLINIC.name} — מרפאת כאב ושיקום. כל הזכויות שמורות.</p>
        </div>
      </div>
    </footer>
  );
}
